import {
  Dispatch,
  KeyboardEvent,
  ReactElement,
  SetStateAction,
  useEffect,
  useLayoutEffect,
  useRef,
  useState
} from 'react';
import { Form } from 'react-bootstrap';
import {
  MdArrowBack,
  MdChatBubbleOutline,
  MdClose,
  MdDeleteOutline,
  MdDoneAll,
  MdExpandLess,
  MdExpandMore,
  MdFlag,
  MdHistory,
  MdSend
} from 'react-icons/md';
import cn from 'classnames';
import { Button } from '@Components/Button/Button';
import { IconButton } from '@Components/IconButton';
import { Spinner } from '@Components/Spinner/Spinner';
import { Appearance, Design, Intent, Size } from '@Components/designConstants';
import { conditionallyPluralizeWithCount } from '@src/utils/text-utils';
import {
  authorLabel,
  formatCommentTime,
  isOwnComment,
  isPendingComment,
  latestCommentId,
  resolverLabel,
  unreadCount
} from '../comment-utils';
import {
  MMMDataQAComment,
  MMMDataQACommentKind,
  MMMDataQACommentThread,
  MMMDataQAResolvedThread
} from '../data-qa-types';
import { FlagRequest } from './CommentsContext';

// Matches the backend's limit on a resolution note.
const MAX_RESOLUTION_NOTE_LENGTH = 1000;

interface CommentsDockProps {
  // The active tab's open thread, if any (where its data is shared with other models, all
  // their open threads on it as one; see mergeOpenThreads), and how to name it ("Media
  // Inputs · DTC MMM").
  thread?: MMMDataQACommentThread;
  scopeLabel: string;
  // The other models whose data on this tab is identical, sharing its thread.
  sharedWith: string[];
  userEmail: string;
  isLoading: boolean;
  loadFailed: boolean;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  // Set while a Flag button is waiting for its reason.
  flagRequest?: FlagRequest;
  onFlagRequestDone: () => void;
  onPost: (body: string) => Promise<unknown>;
  // Resolves the tab's open thread(s); true once done.
  onResolve: (note: string) => Promise<boolean>;
  onDelete: (commentId: number) => Promise<unknown>;
  posting: boolean;
  resolving: boolean;
  // The tab's resolved threads, shown in place of the chat while historyOpen.
  historyOpen: boolean;
  onHistoryOpenChange: (open: boolean) => void;
  resolvedThreads?: MMMDataQAResolvedThread[];
  historyLoading: boolean;
  historyLoadFailed: boolean;
}

// The last comment this viewer has seen in each thread, for the unread badge. Per browser
// only, and optional: storage can be unavailable.
const SEEN_STORAGE_KEY = 'mmmDataQaCommentsSeen';

const isSeenRecord = (value: unknown): value is Record<string, number> =>
  typeof value === 'object' &&
  value !== null &&
  !Array.isArray(value) &&
  Object.values(value).every(v => typeof v === 'number');

const readSeen = (): Record<string, number> => {
  try {
    const parsed: unknown = JSON.parse(localStorage.getItem(SEEN_STORAGE_KEY) ?? '{}');
    return isSeenRecord(parsed) ? parsed : {};
  } catch {
    return {};
  }
};

const writeSeen = (seen: Record<string, number>) => {
  try {
    localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(seen));
  } catch {
    // Unread badges just reset next visit.
  }
};

// The active tab's comment thread, as a small chat window in the lower-right: collapsed to
// a button (with an unread count) until opened. Each tab of each model has its own thread,
// and its own history of resolved threads.
export const CommentsDock = ({
  thread,
  scopeLabel,
  sharedWith,
  userEmail,
  isLoading,
  loadFailed,
  open,
  onOpenChange,
  flagRequest,
  onFlagRequestDone,
  onPost,
  onResolve,
  onDelete,
  posting,
  resolving,
  historyOpen,
  onHistoryOpenChange,
  resolvedThreads,
  historyLoading,
  historyLoadFailed
}: CommentsDockProps) => {
  const [seen, setSeen] = useState(readSeen);
  // Kept here so starting a flag (which swaps the composer) doesn't lose an unsent comment.
  const [commentDraft, setCommentDraft] = useState('');
  // The thread whose Resolve was clicked: its composer asks for the note. Tied to the thread,
  // so switching tabs leaves resolve mode.
  const [resolvingThreadId, setResolvingThreadId] = useState<number | undefined>();
  const lastSeen = thread ? (seen[thread.id] ?? 0) : 0;
  const latest = latestCommentId(thread);

  // An open chat shows the thread, so everything in it counts as seen.
  useEffect(() => {
    if (!open || !thread || latest <= lastSeen) return;
    const next = { ...seen, [thread.id]: latest };
    setSeen(next);
    writeSeen(next);
  }, [open, thread, latest, lastSeen, seen]);

  if (!open) {
    const unread = unreadCount(thread, lastSeen, userEmail);
    const count = thread?.comments.length ?? 0;
    return (
      <div className='data-qa-comments-dock'>
        <Button
          className='data-qa-comments-dock__launcher'
          leadingElement={<MdChatBubbleOutline />}
          aria-label={`Comments on ${scopeLabel}${unread > 0 ? `, ${unread} unread` : ''}`}
          onClick={() => onOpenChange(true)}
        >
          {count > 0 ? count : 'Comments'}
          {unread > 0 && <span className='data-qa-comments-dock__unread'>{unread} new</span>}
        </Button>
      </div>
    );
  }

  // A flag needs the composer, so it takes the panel back from the history.
  if (historyOpen && !flagRequest) {
    return (
      <div className='data-qa-comments-dock'>
        <section
          className='data-qa-comments-panel'
          aria-label={`Resolved threads on ${scopeLabel}`}
        >
          <header className='data-qa-comments-panel__head'>
            <IconButton
              size={Size.SM}
              appearance={Appearance.EMPTY}
              design={Design.NEUTRAL}
              aria-label='Back to comments'
              onClick={() => onHistoryOpenChange(false)}
            >
              <MdArrowBack />
            </IconButton>
            <PanelTitle title='Resolved threads' scopeLabel={scopeLabel} sharedWith={sharedWith} />
            <IconButton
              size={Size.SM}
              appearance={Appearance.EMPTY}
              design={Design.NEUTRAL}
              aria-label='Close comments'
              onClick={() => onOpenChange(false)}
            >
              <MdClose />
            </IconButton>
          </header>
          <ResolvedThreadList
            threads={resolvedThreads}
            isLoading={historyLoading}
            loadFailed={historyLoadFailed}
            userEmail={userEmail}
          />
        </section>
      </div>
    );
  }

  const resolveMode = !flagRequest && thread !== undefined && resolvingThreadId === thread.id;
  let composer: ReactElement;
  if (flagRequest) {
    composer = (
      <FlagComposer
        key='flag'
        flagRequest={flagRequest}
        onDone={onFlagRequestDone}
        posting={posting}
      />
    );
  } else if (resolveMode && thread) {
    composer = (
      <ResolveComposer
        key='resolve'
        resolving={resolving}
        onCancel={() => setResolvingThreadId(undefined)}
        onResolve={async note => {
          if (await onResolve(note)) {
            setResolvingThreadId(undefined);
          }
        }}
      />
    );
  } else {
    composer = (
      <CommentComposer
        key='comment'
        scopeLabel={scopeLabel}
        onPost={onPost}
        draft={commentDraft}
        onDraftChange={setCommentDraft}
      />
    );
  }

  return (
    <div className='data-qa-comments-dock'>
      <section className='data-qa-comments-panel' aria-label={`Comments on ${scopeLabel}`}>
        <header className='data-qa-comments-panel__head'>
          <PanelTitle title='Comments' scopeLabel={scopeLabel} sharedWith={sharedWith} />
          <IconButton
            size={Size.SM}
            appearance={Appearance.EMPTY}
            design={Design.NEUTRAL}
            aria-label='Resolved threads'
            tooltip={{ text: 'Resolved threads' }}
            onClick={() => onHistoryOpenChange(true)}
          >
            <MdHistory />
          </IconButton>
          <IconButton
            size={Size.SM}
            appearance={Appearance.EMPTY}
            design={Design.NEUTRAL}
            aria-label='Resolve thread'
            tooltip={{ text: 'Resolve thread' }}
            disabled={!thread || Boolean(flagRequest)}
            onClick={() => setResolvingThreadId(thread?.id)}
          >
            <MdDoneAll />
          </IconButton>
          <IconButton
            size={Size.SM}
            appearance={Appearance.EMPTY}
            design={Design.NEUTRAL}
            aria-label='Close comments'
            onClick={() => onOpenChange(false)}
          >
            <MdClose />
          </IconButton>
        </header>
        <MessageList
          thread={thread}
          isLoading={isLoading}
          loadFailed={loadFailed}
          userEmail={userEmail}
          onDelete={onDelete}
        />
        {composer}
      </section>
    </div>
  );
};

interface PanelTitleProps {
  title: string;
  scopeLabel: string;
  sharedWith: string[];
}

const PanelTitle = ({ title, scopeLabel, sharedWith }: PanelTitleProps) => (
  <div className='data-qa-comments-panel__title'>
    <span>{title}</span>
    <span className='data-qa-comments-panel__scope'>{scopeLabel}</span>
    {sharedWith.length > 0 && (
      <span className='data-qa-comments-panel__shared'>
        Shared with {sharedWith.join(', ')} · same data
      </span>
    )}
  </div>
);

interface MessageListProps {
  thread?: MMMDataQACommentThread;
  isLoading: boolean;
  loadFailed: boolean;
  userEmail: string;
  onDelete: (commentId: number) => Promise<unknown>;
}

const MessageList = ({ thread, isLoading, loadFailed, userEmail, onDelete }: MessageListProps) => {
  const listRef = useRef<HTMLDivElement>(null);
  const count = thread?.comments.length ?? 0;

  // Newest at the bottom, like a chat: stay scrolled there as comments arrive.
  useLayoutEffect(() => {
    if (listRef.current) {
      listRef.current.scrollTop = listRef.current.scrollHeight;
    }
  }, [count]);

  if (isLoading && !thread) {
    return (
      <div className='data-qa-comments-panel__empty'>
        <Spinner size={24} />
      </div>
    );
  }
  // Polling keeps retrying, so this clears itself once comments load again.
  if (loadFailed && !thread) {
    return (
      <div className='data-qa-comments-panel__empty'>
        Comments couldn&apos;t be loaded. They&apos;ll show here once they load.
      </div>
    );
  }
  if (!thread || count === 0) {
    return (
      <div className='data-qa-comments-panel__empty'>
        No comments on this tab yet. Notes you leave here stay until someone resolves the thread.
      </div>
    );
  }

  return (
    <div className='data-qa-comments-panel__messages' ref={listRef}>
      {thread.comments.map(comment => (
        <Message
          key={comment.id}
          comment={comment}
          own={isOwnComment(comment, userEmail)}
          onDelete={onDelete}
        />
      ))}
    </div>
  );
};

interface MessageProps {
  comment: MMMDataQAComment;
  own: boolean;
  // Left out where comments are read-only (a resolved thread).
  onDelete?: (commentId: number) => Promise<unknown>;
}

const Message = ({ comment, own, onDelete }: MessageProps) => {
  const pending = isPendingComment(comment);
  return (
    <article
      className={cn('data-qa-comment', {
        'data-qa-comment--own': own,
        'data-qa-comment--pending': pending
      })}
    >
      <div className='data-qa-comment__meta'>
        <span className='data-qa-comment__author'>{own ? 'You' : authorLabel(comment)}</span>
        {pending ? (
          <span className='data-qa-comment__status'>Sending…</span>
        ) : (
          <time dateTime={comment.createdAt} title={new Date(comment.createdAt).toLocaleString()}>
            {formatCommentTime(comment.createdAt)}
          </time>
        )}
        {comment.kind === MMMDataQACommentKind.FLAG && (
          <span className='data-qa-comment__flag'>
            <MdFlag /> Flagged
          </span>
        )}
        {own && onDelete && !pending && (
          <IconButton
            className='data-qa-comment__delete'
            size={Size.SM}
            appearance={Appearance.EMPTY}
            design={Design.NEUTRAL}
            aria-label='Delete comment'
            tooltip={{ text: 'Delete comment' }}
            onClick={() => onDelete(comment.id)}
          >
            <MdDeleteOutline />
          </IconButton>
        )}
      </div>
      <p className='data-qa-comment__body'>{comment.body}</p>
    </article>
  );
};

interface ResolvedThreadListProps {
  threads?: MMMDataQAResolvedThread[];
  isLoading: boolean;
  loadFailed: boolean;
  userEmail: string;
}

// Most recently resolved first; the newest starts expanded.
const ResolvedThreadList = ({
  threads,
  isLoading,
  loadFailed,
  userEmail
}: ResolvedThreadListProps) => {
  if (isLoading && !threads) {
    return (
      <div className='data-qa-comments-panel__empty'>
        <Spinner size={24} />
      </div>
    );
  }
  if (loadFailed && !threads) {
    return (
      <div className='data-qa-comments-panel__empty'>
        Resolved threads couldn&apos;t be loaded. Try again in a moment.
      </div>
    );
  }
  if (!threads || threads.length === 0) {
    return (
      <div className='data-qa-comments-panel__empty'>
        No resolved threads on this tab yet. Resolved threads and their notes are kept here.
      </div>
    );
  }
  return (
    <div className='data-qa-comments-panel__messages'>
      {threads.map((thread, i) => (
        <ResolvedThread
          key={thread.id}
          thread={thread}
          userEmail={userEmail}
          defaultExpanded={i === 0}
        />
      ))}
    </div>
  );
};

interface ResolvedThreadProps {
  thread: MMMDataQAResolvedThread;
  userEmail: string;
  defaultExpanded: boolean;
}

const ResolvedThread = ({ thread, userEmail, defaultExpanded }: ResolvedThreadProps) => {
  const [expanded, setExpanded] = useState(defaultExpanded);
  const count = thread.comments.length;
  return (
    <article className='data-qa-resolved-thread'>
      <div className='data-qa-resolved-thread__head'>
        <MdDoneAll />
        <span>
          <span className='data-qa-resolved-thread__resolver'>
            {resolverLabel(thread, userEmail)}
          </span>{' '}
          resolved this ·{' '}
          <time dateTime={thread.resolvedAt} title={new Date(thread.resolvedAt).toLocaleString()}>
            {formatCommentTime(thread.resolvedAt)}
          </time>
        </span>
      </div>
      {thread.resolutionNote && (
        <p className='data-qa-resolved-thread__note'>{thread.resolutionNote}</p>
      )}
      <Button
        className='data-qa-resolved-thread__toggle'
        size={Size.SM}
        appearance={Appearance.EMPTY}
        design={Design.NEUTRAL}
        leadingElement={expanded ? <MdExpandLess /> : <MdExpandMore />}
        aria-expanded={expanded}
        onClick={() => setExpanded(prev => !prev)}
      >
        {expanded ? 'Hide' : 'Show'} {conditionallyPluralizeWithCount(count, 'comment')}
      </Button>
      {expanded && (
        <div className='data-qa-resolved-thread__comments'>
          {thread.comments.map(comment => (
            <Message key={comment.id} comment={comment} own={isOwnComment(comment, userEmail)} />
          ))}
        </div>
      )}
    </article>
  );
};

// Enter sends; Shift+Enter starts a new line.
const onEnterSend = (send: () => void) => (e: KeyboardEvent<HTMLTextAreaElement>) => {
  if (e.key === 'Enter' && !e.shiftKey) {
    e.preventDefault();
    send();
  }
};

interface CommentComposerProps {
  scopeLabel: string;
  onPost: (body: string) => Promise<unknown>;
  draft: string;
  onDraftChange: Dispatch<SetStateAction<string>>;
}

const CommentComposer = ({ scopeLabel, onPost, draft, onDraftChange }: CommentComposerProps) => {
  const text = draft.trim();
  // The comment shows (as pending) the moment it's sent, so the box clears right away; if
  // the post fails, its text comes back for another try (the error is already shown),
  // unless something new has been typed meanwhile.
  const send = async () => {
    if (!text) return;
    onDraftChange('');
    try {
      await onPost(text);
    } catch {
      onDraftChange(current => current || text);
    }
  };
  return (
    <div className='data-qa-comments-panel__composer'>
      <div className='data-qa-comments-panel__input'>
        <Form.Control
          as='textarea'
          rows={2}
          autoFocus
          aria-label={`Comment on ${scopeLabel}`}
          placeholder='Write a comment'
          value={draft}
          onChange={e => onDraftChange(e.target.value)}
          onKeyDown={onEnterSend(send)}
        />
        <IconButton
          size={Size.SM}
          aria-label='Send comment'
          tooltip={{ text: 'Send (Enter)' }}
          disabled={!text}
          onClick={send}
        >
          <MdSend />
        </IconButton>
      </div>
    </div>
  );
};

interface FlagComposerProps {
  flagRequest: FlagRequest;
  onDone: () => void;
  posting: boolean;
}

// Sending flags the tab (its reason is posted once the flag is saved).
const FlagComposer = ({ flagRequest, onDone, posting }: FlagComposerProps) => {
  const [draft, setDraft] = useState(flagRequest.notes);
  const text = draft.trim();
  const send = () => {
    if (!text || posting) return;
    flagRequest.onSubmit(text);
    onDone();
  };
  return (
    <div className='data-qa-comments-panel__composer'>
      <div className='data-qa-comments-panel__flagging'>
        <MdFlag />
        <span>{flagRequest.label}: say what needs attention.</span>
        <Button
          size={Size.SM}
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          onClick={onDone}
        >
          Cancel
        </Button>
      </div>
      <div className='data-qa-comments-panel__input'>
        <Form.Control
          as='textarea'
          rows={2}
          autoFocus
          aria-label='Flag reason'
          placeholder='What needs attention?'
          value={draft}
          onChange={e => setDraft(e.target.value)}
          onKeyDown={onEnterSend(send)}
        />
        <Button
          size={Size.SM}
          design={Intent.WARNING}
          leadingElement={<MdFlag />}
          disabled={!text}
          onClick={send}
        >
          Flag
        </Button>
      </div>
    </div>
  );
};

interface ResolveComposerProps {
  resolving: boolean;
  onCancel: () => void;
  onResolve: (note: string) => Promise<void>;
}

// Resolving asks for an optional note on what was done, kept with the thread in the history.
const ResolveComposer = ({ resolving, onCancel, onResolve }: ResolveComposerProps) => {
  const [note, setNote] = useState('');
  const send = () => {
    if (resolving) return;
    onResolve(note.trim());
  };
  return (
    <div className='data-qa-comments-panel__composer'>
      <div className='data-qa-comments-panel__resolving'>
        <MdDoneAll />
        <span>Resolve this thread. It moves to the tab&apos;s resolved threads.</span>
        <Button
          size={Size.SM}
          appearance={Appearance.EMPTY}
          design={Design.NEUTRAL}
          onClick={onCancel}
        >
          Cancel
        </Button>
      </div>
      <div className='data-qa-comments-panel__input'>
        <Form.Control
          as='textarea'
          rows={2}
          autoFocus
          maxLength={MAX_RESOLUTION_NOTE_LENGTH}
          aria-label='Resolution note (optional)'
          placeholder='What was done? (optional)'
          value={note}
          onChange={e => setNote(e.target.value)}
          onKeyDown={onEnterSend(send)}
        />
        <Button size={Size.SM} leadingElement={<MdDoneAll />} loading={resolving} onClick={send}>
          Resolve
        </Button>
      </div>
    </div>
  );
};
